import { v4 as uuidv4 } from 'uuid';

export class StyleProfile {
  constructor(params = {}) {
    // Validate required fields
    if (!params.name) {
      throw new Error('Name is required');
    }

    // Validate writing style
    const validStyles = ['formal', 'conversational', 'technical'];
    if (params.writingStyle && !validStyles.includes(params.writingStyle)) {
      throw new Error(`Invalid writing style: ${params.writingStyle}`);
    }

    // Validate tone
    const validTones = ['professional', 'friendly', 'authoritative'];
    if (params.tone && !validTones.includes(params.tone)) {
      throw new Error(`Invalid tone: ${params.tone}`);
    }

    // Validate complexity
    const validComplexities = ['beginner', 'intermediate', 'advanced'];
    if (params.complexity && !validComplexities.includes(params.complexity)) {
      throw new Error(`Invalid complexity: ${params.complexity}`);
    }

    // Set properties with defaults
    this.id = params.id || uuidv4();
    this.name = params.name;
    this.writingStyle = params.writingStyle || 'formal';
    this.tone = params.tone || 'professional';
    this.complexity = params.complexity || 'intermediate';
    this.sentenceLength = params.sentenceLength || 'medium';
    this.vocabularyLevel = params.vocabularyLevel || 'standard';
    this.isPreset = params.isPreset || false;
    this.userId = params.userId || null;
    this.createdAt = params.createdAt || new Date();
    this.updatedAt = params.updatedAt || new Date();
  }

  isValid() {
    return !!(
      this.name &&
      this.writingStyle &&
      this.tone &&
      this.complexity &&
      this.sentenceLength &&
      this.vocabularyLevel
    );
  }

  validateTone(tone) {
    const validTones = ['professional', 'friendly', 'authoritative'];
    return validTones.includes(tone);
  }

  validateWritingStyle(style) {
    const validStyles = ['formal', 'conversational', 'technical'];
    return validStyles.includes(style);
  }

  validateComplexity(complexity) {
    const validComplexities = ['beginner', 'intermediate', 'advanced'];
    return validComplexities.includes(complexity);
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      writingStyle: this.writingStyle,
      tone: this.tone,
      complexity: this.complexity,
      sentenceLength: this.sentenceLength,
      vocabularyLevel: this.vocabularyLevel,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      isPreset: this.isPreset,
      userId: this.userId
    };
  }

  static fromJSON(json) {
    return new StyleProfile(json);
  }

  clone() {
    const cloned = new StyleProfile({
      ...this.toJSON(),
      id: uuidv4(), // New ID for clone
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return cloned;
  }

  update(updates) {
    Object.keys(updates).forEach(key => {
      if (key === 'writingStyle' && !this.validateWritingStyle(updates[key])) {
        throw new Error(`Invalid writing style: ${updates[key]}`);
      }
      if (key === 'tone' && !this.validateTone(updates[key])) {
        throw new Error(`Invalid tone: ${updates[key]}`);
      }
      if (key === 'complexity' && !this.validateComplexity(updates[key])) {
        throw new Error(`Invalid complexity: ${updates[key]}`);
      }
      this[key] = updates[key];
    });
    this.updatedAt = new Date();
  }

  static getPresets() {
    return [
      new StyleProfile({
        name: 'Corporate',
        writingStyle: 'formal',
        tone: 'professional',
        complexity: 'intermediate',
        sentenceLength: 'medium',
        vocabularyLevel: 'standard',
        isPreset: true,
        description: 'Formal writing style suitable for board communications and official reports'
      }),
      new StyleProfile({
        name: 'Client-Friendly',
        writingStyle: 'conversational',
        tone: 'friendly',
        complexity: 'beginner',
        sentenceLength: 'short',
        vocabularyLevel: 'simple',
        isPreset: true,
        description: 'Approachable style for retail investors and general clients'
      }),
      new StyleProfile({
        name: 'Expert Analysis',
        writingStyle: 'technical',
        tone: 'authoritative',
        complexity: 'advanced',
        sentenceLength: 'long',
        vocabularyLevel: 'sophisticated',
        isPreset: true,
        description: 'Detailed technical analysis for institutional clients'
      }),
      new StyleProfile({
        name: 'Educational',
        writingStyle: 'conversational',
        tone: 'professional',
        complexity: 'intermediate',
        sentenceLength: 'medium',
        vocabularyLevel: 'standard',
        isPreset: true,
        description: 'Educational content that explains concepts clearly'
      })
    ];
  }

  generateSystemPrompt() {
    const complexityDescriptions = {
      beginner: 'simple terms and avoid technical jargon',
      intermediate: 'standard financial terminology with brief explanations',
      advanced: 'sophisticated financial concepts and technical details'
    };

    const sentenceLengthDescriptions = {
      short: 'short, punchy sentences (10-15 words)',
      medium: 'medium sentence length (15-20 words)',
      long: 'longer, detailed sentences (20-30 words)'
    };

    const vocabularyDescriptions = {
      simple: 'simple, everyday vocabulary',
      standard: 'standard vocabulary level',
      sophisticated: 'sophisticated and technical vocabulary'
    };

    return `You are writing in a ${this.writingStyle} writing style with a ${this.tone} tone.
Use ${this.complexity} complexity level with ${complexityDescriptions[this.complexity]}.
Maintain ${sentenceLengthDescriptions[this.sentenceLength]} and use ${vocabularyDescriptions[this.vocabularyLevel]}.
CRITICAL: Preserve ALL numbers, percentages, dates, and factual information exactly as provided.`;
  }

  getStyleInstructions() {
    const instructions = [];

    // Writing style instructions
    if (this.writingStyle === 'formal') {
      instructions.push('Use formal language and structure');
      instructions.push('Avoid contractions and colloquialisms');
    } else if (this.writingStyle === 'conversational') {
      instructions.push('Use conversational language');
      instructions.push('Include personal pronouns when appropriate');
    } else if (this.writingStyle === 'technical') {
      instructions.push('Use precise technical terminology');
      instructions.push('Include detailed analysis');
    }

    // Tone instructions
    if (this.tone === 'friendly') {
      instructions.push('Maintain a warm and friendly tone');
      instructions.push('Use engaging language');
    } else if (this.tone === 'professional') {
      instructions.push('Keep a professional tone throughout');
    } else if (this.tone === 'authoritative') {
      instructions.push('Project confidence and expertise');
    }

    // Complexity instructions
    if (this.complexity === 'beginner') {
      instructions.push('Use simple terms and avoid jargon');
      instructions.push('Explain financial concepts clearly');
    } else if (this.complexity === 'advanced') {
      instructions.push('Include sophisticated analysis');
      instructions.push('Assume reader has financial knowledge');
    }

    return instructions.join('. ') + '.';
  }
}